import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ModalButtons } from 'components/reusables/modalButtons'
import { Modal } from 'components/reusables/modal'
import { useAppDispatch } from 'store/hooks'
import { slices } from 'store/slices'
import {
  IQuestionBankQuestion,
  IQuestionBankSection,
} from 'interfaces/questionBank'
import { initialQuestionBankQuestion } from 'store/slices/initialData'
import {
  isValidImage,
  isValidVideo,
  isValidHTML,
} from 'helpers/multimediaContent.helper'
import { MultimediaContentType } from 'enum'
import MultimediaContent from './multimediaContent'
import Inputs from './inputs'
import AnswerExplanation from './answerExplanation'
import Answers from './answers'

interface IProps {
  data?: IQuestionBankQuestion
  section: IQuestionBankSection
  close: () => void
}

const ManageQuestion = ({ data, section, close }: IProps) => {
  const sectionOrder = section.order
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const translationPrefix =
    'administration.blocks.type.questionBankV2.sections.manageQuestion'
  const [canContinue, setCanContinue] = useState(false)
  const [primaryTooltip, setPrimaryTooltip] = useState('')
  const [question, setQuestion] = useState<IQuestionBankQuestion>(
    data || {
      ...initialQuestionBankQuestion,
      score: section.randomOrder
        ? section.scoreForEachQuestion
        : initialQuestionBankQuestion.score,
    }
  )

  useEffect(() => {
    setCanContinue(questionIsValid())
  }, [question])

  const questionIsValid = () => {
    if (question.name.trim().length === 0) {
      setPrimaryTooltip(t(`${translationPrefix}.buttons.tooltip.hasNoName`))
      return false
    }

    if (question.answers.length === 0) {
      setPrimaryTooltip(t(`${translationPrefix}.buttons.tooltip.hasNoAnswers`))
      return false
    }

    if (question.answers.some((answer) => answer.name.trim().length === 0)) {
      setPrimaryTooltip(
        t(`${translationPrefix}.buttons.tooltip.hasNoValidAnswers`)
      )
      return false
    }

    if (question.answers.every((answer) => !answer.correct)) {
      setPrimaryTooltip(
        t(`${translationPrefix}.buttons.tooltip.hasNoCorrectAnswer`)
      )
      return false
    }

    const multimediaContentType = question.multimediaContent?.type
    const multimediaContent = question.multimediaContent?.value
    const hasNoValidImage =
      multimediaContentType === MultimediaContentType.image &&
      !isValidImage(multimediaContent)
    const hasNoValidVideo =
      multimediaContentType === MultimediaContentType.video &&
      !isValidVideo(multimediaContent)
    const hasNoValidHTML =
      multimediaContentType === MultimediaContentType.code &&
      !isValidHTML(multimediaContent)

    if (
      multimediaContent &&
      (multimediaContent.trim().length === 0 ||
        hasNoValidImage ||
        hasNoValidVideo ||
        hasNoValidHTML)
    ) {
      setPrimaryTooltip(
        t(`${translationPrefix}.buttons.tooltip.hasNoValidMultimediaContent`)
      )
      return false
    }

    if (
      question.showAnswerExplanation &&
      question.answerExplanation.trim().length === 0
    ) {
      setPrimaryTooltip(
        t(`${translationPrefix}.buttons.tooltip.hasNoValidAnswerExplanation`)
      )
      return false
    }

    setPrimaryTooltip('')
    return true
  }

  const next = () => {
    if (data) {
      dispatch(
        slices.questionBank.editQuestion({
          sectionOrder: sectionOrder,
          question: question,
        })
      )
    } else {
      dispatch(
        slices.questionBank.addQuestion({
          sectionOrder: sectionOrder,
          question: question,
        })
      )
    }
    close()
  }

  const actions = (
    <ModalButtons
      primaryText={t(`${translationPrefix}.buttons.${data ? 'edit' : 'add'}`)}
      primaryAction={next}
      disablePrimaryButton={!canContinue}
      primaryTooltip={primaryTooltip}
      secondaryText={t(`${translationPrefix}.buttons.cancel`)}
      secondaryAction={close}
    />
  )

  return (
    <Modal
      title={t(`${translationPrefix}.title.${data ? 'edit' : 'add'}`)}
      actions={actions}
      maxheight={480}
      maxwidth={800}
      close={close}
    >
      <>
        <Inputs question={question} setQuestion={setQuestion} />
        <MultimediaContent question={question} setQuestion={setQuestion} />
        <Answers question={question} setQuestion={setQuestion} />
        <AnswerExplanation question={question} setQuestion={setQuestion} />
      </>
    </Modal>
  )
}

export default ManageQuestion
